import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, IconButton, Stack, Paper, CircularProgress, Chip, Button,
} from '@mui/material';
import { Send, Science, SmartToy, RestartAlt } from '@mui/icons-material';
import { api } from '../config/api';

const PREGUNTAS_BASE = ['Hola, ¿qué venden?', '¿Cuál es el horario?', '¿Dónde están ubicados?', '¿Qué medios de pago aceptan?', 'Quiero hablar con una persona'];

interface Msg { role: 'user' | 'bot'; text: string; }

const Playground: React.FC = () => {
  const [mensajes, setMensajes] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const [params] = useSearchParams();
  // Preguntas sugeridas: las del análisis de la base (?q=a|b|c) o unas genéricas.
  const sugeridas = (params.get('q') || '').split('|').map((s) => s.trim()).filter(Boolean);
  const preguntas = sugeridas.length ? sugeridas : PREGUNTAS_BASE;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes, loading]);

  const enviar = async (directo?: string) => {
    const texto = (directo ?? input).trim();
    if (!texto || loading) return;
    setMensajes((m) => [...m, { role: 'user', text: texto }]);
    setInput('');
    setLoading(true);
    try {
      const { data } = await api.post('/agente/playground', { mensaje: texto });
      setMensajes((m) => [...m, { role: 'bot', text: data.respuesta }]);
    } catch (err: any) {
      setMensajes((m) => [...m, { role: 'bot', text: err.response?.data?.error || 'Error al generar respuesta' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Science color="primary" />
        <Typography variant="h4">Playground</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Prueba tu agente aquí antes de activarlo en WhatsApp. Usa tu prompt y tu base de conocimiento.
      </Typography>

      <Box sx={{ maxWidth: 680, mx: 'auto', mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {sugeridas.length ? 'Preguntas sugeridas por el análisis de tu base:' : 'Prueba con estas preguntas:'}
          </Typography>
          {mensajes.length > 0 && <Button size="small" startIcon={<RestartAlt />} onClick={() => setMensajes([])}>Reiniciar</Button>}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {preguntas.map((q) => <Chip key={q} label={q} variant="outlined" onClick={() => enviar(q)} disabled={loading} sx={{ bgcolor: '#fff' }} />)}
        </Stack>
      </Box>

      <Card sx={{ maxWidth: 680, mx: 'auto' }}>
        <Box sx={{ bgcolor: '#075E54', color: 'white', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy />
          <Typography variant="subtitle1">Tu Agente (simulación)</Typography>
        </Box>
        <CardContent sx={{ bgcolor: '#ECE5DD', minHeight: 380, maxHeight: 460, overflowY: 'auto' }}>
          {mensajes.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 6 }}>
              Escribe un mensaje como si fueras un cliente 👇
            </Typography>
          )}
          <Stack spacing={1.5}>
            {mensajes.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <Paper sx={{
                  p: 1.2, px: 1.6, maxWidth: '78%',
                  bgcolor: m.role === 'user' ? '#DCF8C6' : 'white',
                  borderRadius: 2,
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Paper sx={{ p: 1.2, px: 2, bgcolor: 'white', borderRadius: 2 }}><CircularProgress size={16} /></Paper>
              </Box>
            )}
            <div ref={endRef} />
          </Stack>
        </CardContent>
        <Box sx={{ p: 1.5, display: 'flex', gap: 1, borderTop: '1px solid #e0e0e0' }}>
          <TextField
            fullWidth size="small" placeholder="Escribe un mensaje..." value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          />
          <IconButton color="primary" onClick={() => enviar()} disabled={loading}><Send /></IconButton>
        </Box>
      </Card>
    </Box>
  );
};

export default Playground;
